import { Controller } from "@hotwired/stimulus"

// Collapsible game groups for the admin tracks sidebar list.
//
// The list lives inside <turbo-frame id="tracks_list">. Every search or
// navigation that re-renders the frame calls connect() again, so all open/close
// state is persisted to localStorage and restored on connect() — bare class
// toggles would snap back to the server default (hidden) on every re-render.
//
// Storage key: "admin-tracks:open-groups" → JSON { [game]: true }
// A group is closed unless stored as open.
// Exceptions (computed at connect time / on filteringValueChanged):
//   - filteringValue === true  → force ALL groups open
//   - game === selectedGameValue → auto-expand the selected track's group
//
// The admin-track-filter controller drives filteringValue by writing
// data-admin-track-groups-filtering-value on this element, which Stimulus
// picks up as a value change and calls filteringValueChanged().
export default class extends Controller {
  static targets = ["group", "chevron", "list"]
  static values = { selectedGame: String, filtering: Boolean }

  connect() {
    this.#restoreState()
  }

  toggle(event) {
    const game = event.currentTarget.dataset.game
    const open = this.#loadOpenSet()
    const isOpen = !!open[game]
    const willOpen = !isOpen

    if (willOpen) {
      open[game] = true
    } else {
      delete open[game]
    }
    this.#saveOpenSet(open)
    this.#applyState(game, willOpen)
  }

  // Called by Stimulus when data-admin-track-groups-filtering-value changes.
  // The admin-track-filter controller writes this attribute to force all groups
  // open while any client-side filter is active, and to restore collapse state
  // once filters are cleared.
  filteringValueChanged() {
    this.#restoreState()
  }

  // ── private ──────────────────────────────────────────────────────────────

  // Re-apply open/closed state for every group based on current filteringValue,
  // localStorage, and selectedGameValue. Called on connect() and whenever
  // filteringValue changes.
  #restoreState() {
    const open = this.#loadOpenSet()
    this.groupTargets.forEach((group) => {
      const game = group.dataset.game
      const shouldOpen =
        this.filteringValue || open[game] || game === this.selectedGameValue
      this.#applyState(game, shouldOpen)
    })
  }

  #applyState(game, open) {
    // list target
    const list = this.listTargets.find((el) => el.dataset.game === game)
    if (list) {
      list.classList.toggle("hidden", !open)
    }
    // chevron target
    const chevron = this.chevronTargets.find((el) => el.dataset.game === game)
    if (chevron) {
      chevron.classList.toggle("rotate-90", open)
    }
  }

  #loadOpenSet() {
    try {
      const raw = localStorage.getItem("admin-tracks:open-groups")
      return raw ? JSON.parse(raw) : {}
    } catch {
      return {}
    }
  }

  #saveOpenSet(openSet) {
    try {
      localStorage.setItem("admin-tracks:open-groups", JSON.stringify(openSet))
    } catch {
      // localStorage unavailable — silently ignore
    }
  }
}
